import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { AvatarGroup } from 'primereact/avatargroup';
import { Avatar } from 'primereact/avatar';
import { Tooltip } from 'primereact/tooltip';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import listPlugin from '@fullcalendar/list';
import momentPlugin from '@fullcalendar/moment';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import moment from 'moment';

import { useCalendarState, useCalendarUI } from '../../context/CalendarContext';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getUserTimezone, parseDateRange, getCurrentDateInTimezone } from '../../utils/timezoneHelpers';
import { isMoreLinkEvent } from 'modules/calendarv2/utils/eventFormatters';
import { getTenantDetails } from 'utils/utils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { getIconClass } from 'modules/calendarv2/utils/eventColorhelper';
import EventContent from './EventContent';
import ImageComponent from 'shared/ImageComponent';
import useWindowSize from 'hooks/useWindowSize';

const CalendarView = () => {
  const { viewDateState, filters, selectedUsers, settings, caseDetails, setViewDateState } = useCalendarState();
  const { currentView, currentDate, dateRange } = viewDateState || {};
  // UI actions
  const { openEventViewModal, openEventFormModal, openDayEventsPopup } = useCalendarUI();

  const userContext = useUserDetailsContext();
  const timezone = getUserTimezone(userContext);
  const [tenantId] = getTenantDetails();
  const history = useHistory();
  const { width } = useWindowSize();

  // Ref to access FullCalendar API
  const calendarRef = useRef(null);

  // Track if view is transitioning
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Fetch events using React Query
  const { events, isLoading, isError, error } = useCalendarEvents({
    view: currentView,
    dateRange,
    filters,
    selectedUsers,
    timezone,
    case_id: caseDetails?.case_id,
    enabled: true,
  });

  // Handle view change from FullCalendar toolbar
  const handleViewChange = useCallback(() => setIsTransitioning(false), [currentView]);

  // Custom Year button click handler - shows loader then changes view
  const handleYearButtonClick = useCallback(() => {
    if (currentView === 'multiMonthYear') return;

    setIsTransitioning(true);
    // Use requestAnimationFrame to ensure the loader renders before view change
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
          calendarApi.changeView('multiMonthYear');
        }
      });
    });
  }, [currentView]);

  // Handle view changes
  const handleDatesSet = useCallback(
    (dateInfo) => {
      const range = parseDateRange(dateInfo, timezone);
      setViewDateState((prev) => ({
        ...prev,
        currentView: dateInfo.view.type,
        currentDate: dateInfo.start,
        dateRange: range,
      }));
    },
    [timezone, setViewDateState]
  );

  // Handle event click
  const handleEventClick = useCallback(
    (clickInfo) => {
      const event = clickInfo.event;
      const { extendedProps } = event;
      if (extendedProps.calendar_type === 'user') {
        const url = `/contacts/${extendedProps.contact_id}`;
        history.push(url);
        return;
      }
      if (extendedProps.calendar_type === 'case') {
        const url = `/cases/${extendedProps.case_id}`;
        history.push(url);
        return;
      }
      if (isMoreLinkEvent(event)) {
        return;
      }
      // Open event view modal
      openEventViewModal(event);
    },
    [openEventViewModal, history]
  );

  // Handle date select (drag to create)
  const handleDateSelect = useCallback(
    (selectInfo) => {
      if (selectInfo?.jsEvent?.target?.classList?.contains('fc-daygrid-day-number')) {
        const calendarApi = selectInfo.view.calendar;
        calendarApi.changeView('timeGridDay', selectInfo.startStr);
        return;
      }
      // If the view is a month or year view, current time is in the timezone of the view
      if (selectInfo.view?.type === 'dayGridMonth' || selectInfo.view?.type === 'multiMonthYear') {
        const nowInTz = moment().tz(timezone);
        // Get current minutes in the calendar's timezone and round to nearest 15
        const currentMinutes = nowInTz.minute();
        const remainder = currentMinutes % 15;
        const roundedMinutes = remainder === 0 ? currentMinutes : currentMinutes + (15 - remainder);
        // merge date + current time with rounded minutes
        const datetimeInTz = moment.tz(selectInfo.start, timezone).hour(nowInTz.hour()).minute(roundedMinutes).second(0).millisecond(0);
        // convert to UTC
        const utcStart = datetimeInTz.utc().format();
        openEventFormModal('create', {
          start: utcStart,
        });
        return;
      }

      openEventFormModal('create', {
        start: selectInfo.start,
        end: selectInfo.end,
        allDay: selectInfo.allDay,
      });
    },
    [openEventFormModal]
  );

  // Memoize event content renderer (includes list view rendering)
  const eventContentRenderer = useCallback(
    (eventInfo) => {
      if (currentView === 'listWeek') {
        const { event } = eventInfo;
        const extendedProps = event.extendedProps || {};
        const attendees = extendedProps.participants || [];
        const attendeeCount = Array.isArray(attendees) ? attendees.length : 0;

        // Check if we're on mobile/small screen
        const isMobile = window.innerWidth < 768;

        if (isMobile) {
          // Mobile card layout - include time in the card
          return (
            <div className="fc-list-event-mobile-card">
              <div className="fc-list-event-card-header">
                <div className="fc-list-event-indicator" style={{ backgroundColor: event.backgroundColor || '#517dda' }}></div>
                <div className="fc-list-event-card-title">
                  <i
                    className={`${getIconClass(extendedProps.calendar_type, extendedProps.is_google_event)} me-2`}
                    style={{ color: 'var(--black-500)' }}
                  ></i>
                  {extendedProps.calendar_type === 'case'
                    ? 'Statute Of Limitations'
                    : extendedProps.calendar_type === 'user'
                    ? 'Birthday: ' + extendedProps.contact_name
                    : event.title}
                </div>
                <div className="fc-list-event-card-time">{event.allDay ? 'All Day' : `${eventInfo.timeText}`}</div>
              </div>
              <div className="fc-list-event-card-content">
                <div className="fc-list-event-card-row">
                  <div className="fc-list-event-card-label">Case/Lead:</div>
                  <div className="fc-list-event-card-value">{extendedProps.case_name || 'N/A'}</div>
                </div>
                {extendedProps.practiceArea && (
                  <div className="fc-list-event-card-row">
                    <div className="fc-list-event-card-label">Practice Area:</div>
                    <div className="fc-list-event-card-value">{extendedProps.practiceArea}</div>
                  </div>
                )}
                {extendedProps.event_type && (
                  <div className="fc-list-event-card-row">
                    <div className="fc-list-event-card-label">Type:</div>
                    <div className="fc-list-event-card-value">{extendedProps.event_type}</div>
                  </div>
                )}
                {extendedProps.due_date && (
                  <div className="fc-list-event-card-row">
                    <div className="fc-list-event-card-label">Due Date:</div>
                    <div className="fc-list-event-card-value">{convertDateFromApiToUserTimezone(extendedProps.due_date, timezone)}</div>
                  </div>
                )}
                <div className="fc-list-event-card-row">
                  <div className="fc-list-event-card-label">Attendees:</div>
                  <div className="fc-list-event-card-value">
                    {attendeeCount > 0 ? (
                      <>
                        <div className="attendees-count-mobile">
                          {attendeeCount} {attendeeCount === 1 ? 'person' : 'people'}
                        </div>
                        <div className="attendees-names-mobile">
                          <AvatarGroup>
                            {attendees.map((user, i) => {
                              if (i < 4) {
                                const stableId = `${i}-${user?.contact_id || user?.firm_user_id || i}`;
                                return (
                                  <ImageComponent
                                    key={stableId}
                                    filePath={
                                      user?.contact_id || user?.firm_user_id
                                        ? `${tenantId}/${user?.contact_id ? user?.contact_id : user?.firm_user_id}/profile_image`
                                        : null
                                    }
                                    fileName={user.name}
                                    className={`me-1 task-avatar assignee-${stableId}`}
                                    toolTipTarget={`.assignee-${stableId}`}
                                    toolTipContent={
                                      <>
                                        {user?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                                        {user.name}
                                      </>
                                    }
                                    tooltipPosition="top"
                                    showTooltip={true}
                                  />
                                );
                              }
                              return null;
                            })}
                            {attendees.length > 4 && (
                              <>
                                <Avatar
                                  label={`+${attendees.length - 4}`}
                                  className={`me-1 pointer more-attendees-mobile-${event.id}`}
                                  shape="circle"
                                />
                                <Tooltip target={`.more-attendees-mobile-${event.id}`} position="top" className="case-tooltip mb-3">
                                  <div className="attendees-tooltip-list">
                                    {attendees.slice(4).map((user, idx) => (
                                      <div key={idx} className="attendees-tooltip-item">
                                        {user?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                                        {user.name}
                                      </div>
                                    ))}
                                  </div>
                                </Tooltip>
                              </>
                            )}
                          </AvatarGroup>
                        </div>
                      </>
                    ) : (
                      <div>No attendees</div>
                    )}
                  </div>
                </div>
                <div className="fc-list-event-card-row">
                  <div className="fc-list-event-card-label">Location:</div>
                  <div className="fc-list-event-card-value">{extendedProps.location || 'N/A'}</div>
                </div>
              </div>
            </div>
          );
        }

        // Desktop table layout (existing code)
        return (
          <div className="fc-list-event-custom-wrapper">
            <div className="fc-list-event-custom-cell event-cell">
              <div className="fc-list-event-indicator" style={{ backgroundColor: event.backgroundColor || '#517dda' }}></div>
              <div className="fc-list-event-details">
                <div className="fc-list-event-title">
                  <i
                    className={`${getIconClass(extendedProps.calendar_type, extendedProps.is_google_event)} me-2`}
                    style={{ color: 'var(--black-500)' }}
                  ></i>
                  {extendedProps.calendar_type === 'case'
                    ? 'Statute Of Limitations'
                    : extendedProps.calendar_type === 'user'
                    ? 'Birthday: ' + extendedProps.contact_name
                    : event.title}
                </div>
                <div className="d-flex align-items-center flex-wrap gap-3">
                  {extendedProps.case_name && (
                    <div className="fc-list-event-subtitle">
                      Case/Lead: <span className="text-bold">{extendedProps.case_name}</span>
                    </div>
                  )}
                  {extendedProps.practiceArea && (
                    <div className="fc-list-event-subtitle">
                      Practice area: <span className="text-bold">{extendedProps.practiceArea}</span>
                    </div>
                  )}
                  {extendedProps.event_type && (
                    <div className="fc-list-event-subtitle">
                      Type: <span className="text-bold">{extendedProps.event_type}</span>
                    </div>
                  )}
                  {extendedProps.due_date && (
                    <div className="fc-list-event-subtitle">
                      Due date: <span className="text-bold">{convertDateFromApiToUserTimezone(extendedProps.due_date, timezone)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="fc-list-event-custom-cell attendees-cell">
              {attendeeCount > 0 ? (
                <>
                  <div className="attendees-count">
                    {attendeeCount} {attendeeCount === 1 ? 'person' : 'people'}
                  </div>
                  <div className="attendees-names">
                    <AvatarGroup>
                      {attendees.map((user, i) => {
                        if (i < 4) {
                          // Use stable ID instead of generating new UUID on every render
                          const stableId = `${i}-${user?.contact_id || user?.firm_user_id || i}`;
                          return (
                            <ImageComponent
                              key={stableId}
                              filePath={
                                user?.contact_id || user?.firm_user_id
                                  ? `${tenantId}/${user?.contact_id ? user?.contact_id : user?.firm_user_id}/profile_image`
                                  : null
                              }
                              fileName={user.name}
                              className={`me-1 task-avatar assignee-${stableId}`}
                              toolTipTarget={`.assignee-${stableId}`}
                              toolTipContent={
                                <>
                                  {user?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                                  {user.name}
                                </>
                              }
                              tooltipPosition="top"
                              showTooltip={true}
                            />
                          );
                        }
                        return null;
                      })}
                      {attendees.length > 4 && (
                        <>
                          <Avatar
                            label={`+${attendees.length - 4}`}
                            className={`me-1 pointer more-attendees-desktop-${event.id}`}
                            shape="circle"
                          />
                          <Tooltip target={`.more-attendees-desktop-${event.id}`} position="top" className="case-tooltip mb-3">
                            <div className="attendees-tooltip-list">
                              {attendees.slice(4).map((user, idx) => (
                                <div key={idx} className="attendees-tooltip-item">
                                  {user?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                                  {user.name}
                                </div>
                              ))}
                            </div>
                          </Tooltip>
                        </>
                      )}
                    </AvatarGroup>
                  </div>
                </>
              ) : (
                <div className="attendees-names">No attendees</div>
              )}
            </div>
            <div className="fc-list-event-custom-cell location-cell">Location: {extendedProps.location || 'N/A'}</div>
          </div>
        );
      }

      if (currentView === 'multiMonthYear') {
        if (isMoreLinkEvent(eventInfo.event)) {
          const onClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            openDayEventsPopup(eventInfo?.event?._def?.extendedProps?.date);
          };
          return (
            <a onClick={onClick} title={eventInfo.event.title} className="fc-daygrid-more-link fc-more-link custom-more-link">
              {eventInfo.event.title}
            </a>
          );
        }
        return <></>;
      }

      return <EventContent eventInfo={eventInfo} openDayEventsPopup={openDayEventsPopup} />;
    },
    [currentView, openDayEventsPopup]
  );

  const getNowInUserTimezone = useCallback(() => {
    return getCurrentDateInTimezone(timezone);
  }, [timezone]);

  // Calculate multiMonthMaxColumns based on screen width
  const getColumnsForWidth = useCallback((screenWidth) => {
    if (screenWidth < 768) return 1; // Mobile
    if (screenWidth < 1024) return 2; // Tablet
    if (screenWidth < 1440) return 2; // Small Desktop
    return 3; // Large Desktop
  }, []);

  // Static options that never change - memoized once
  const staticCalendarOptions = useMemo(
    () => ({
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin, listPlugin, momentPlugin, momentTimezonePlugin],
      moreLinkClick: 'day',
      dayMaxEventRows: false,
      dayMaxEvents: false,
      eventMaxStack: Infinity,
      headerToolbar: {
        start: 'today prev,next',
        center: 'title',
        end: 'timeGridDay,timeGridWeek,dayGridMonth,customYearButton,listWeek',
      },
      buttonText: {
        today: 'Today',
        day: 'Day',
        week: 'Week',
        month: 'Month',
        list: 'Agenda',
      },
      allDayText: 'All Day',
      height: 'auto',
      editable: false,
      selectable: true,
      selectMirror: true,
      nowIndicator: true,
      moreLinkContent: () => <></>,
      // Display event times
      displayEventTime: true,
      displayEventEnd: true,
      // Time format for event display - applies to all views
      eventTimeFormat: {
        hour: 'numeric',
        minute: '2-digit',
        meridiem: 'short',
      },
      // Slot label format for time grid views
      slotLabelFormat: {
        hour: 'numeric',
        minute: '2-digit',
        meridiem: 'short',
      },
    }),
    []
  );

  // Custom buttons (depends on click handler)
  const customButtons = useMemo(
    () => ({
      customYearButton: {
        text: 'Year',
        click: handleYearButtonClick,
      },
    }),
    [handleYearButtonClick]
  );

  // View-specific configuration
  const viewsConfig = useMemo(
    () => ({
      listWeek: {
        type: 'list',
        duration: { days: 7 },
        titleFormat: { month: 'short', day: 'numeric', year: 'numeric' },
        listDayFormat: 'dddd, D MMM YYYY',
        listDaySideFormat: false,
        // Consistent time format for agenda/list view
        eventTimeFormat: {
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'short',
        },
      },
      multiMonthYear: {
        dayMaxEvents: false,
        dayMaxEventRows: false,
        eventDisplay: 'block',
        multiMonthMaxColumns: getColumnsForWidth(width),
      },
    }),
    [width, getColumnsForWidth]
  );

  // Settings-dependent options (using API format keys)
  const settingsOptions = useMemo(
    () => ({
      weekends: settings?.show_weekends !== false,
      slotMinTime: settings?.show_business_times_only ? '09:00:00' : '00:00:00',
      slotMaxTime: settings?.show_business_times_only ? '17:00:00' : '24:00:00',
    }),
    [settings]
  );

  // Event handlers config
  const eventHandlers = useMemo(
    () => ({
      datesSet: handleDatesSet,
      eventClick: handleEventClick,
      select: handleDateSelect,
      viewDidMount: handleViewChange,
    }),
    [handleDatesSet, handleEventClick, handleDateSelect, handleViewChange]
  );

  // Final merged calendar options - minimal dependencies
  const calendarOptions = useMemo(
    () => ({
      ...staticCalendarOptions,
      customButtons,
      views: viewsConfig,
      initialView: currentView,
      initialDate: currentDate,
      timeZone: timezone,
      now: getNowInUserTimezone,
      events: events,
      eventContent: eventContentRenderer,
      ...settingsOptions,
      ...eventHandlers,
    }),
    [
      staticCalendarOptions,
      customButtons,
      viewsConfig,
      currentView,
      currentDate,
      timezone,
      getNowInUserTimezone,
      events,
      eventContentRenderer,
      settingsOptions,
      eventHandlers,
    ]
  );

  // Manually update FullCalendar events when they change to ensure proper re-render
  // This fixes the issue where React Query cached data doesn't trigger FullCalendar updates
  useEffect(() => {
    if (calendarRef.current && Array.isArray(events)) {
      const calendarApi = calendarRef.current.getApi();

      // Get current event sources and remove them
      const eventSources = calendarApi.getEventSources();
      eventSources.forEach((source) => source.remove());

      // Remove any remaining events
      const currentEvents = calendarApi.getEvents();
      currentEvents.forEach((event) => event.remove());

      // Add new events as a batch
      calendarApi.addEventSource(events);
    }
  }, [events]);

  // Manage customYearButton active state
  useEffect(() => {
    if (calendarRef.current) {
      const calendarEl = calendarRef.current.getApi().el;
      const yearButton = calendarEl.querySelector('.fc-customYearButton-button');

      if (yearButton) {
        if (currentView === 'multiMonthYear') {
          yearButton.classList.add('fc-button-active');
        } else {
          yearButton.classList.remove('fc-button-active');
        }
      }
    }
  }, [currentView]);

  // Update calendar options when settings change
  useEffect(() => {
    if (calendarRef.current && settings) {
      const calendarApi = calendarRef.current.getApi();

      // Update weekends visibility
      calendarApi.setOption('weekends', settings.show_weekends !== false);

      // Update business hours
      if (settings.show_business_times_only) {
        calendarApi.setOption('slotMinTime', '09:00:00');
        calendarApi.setOption('slotMaxTime', '17:00:00');
      } else {
        calendarApi.setOption('slotMinTime', '00:00:00');
        calendarApi.setOption('slotMaxTime', '24:00:00');
      }
    }
  }, [settings?.show_weekends, settings?.show_business_times_only]);

  // Update calendar view when currentView changes
  useEffect(() => {
    if (calendarRef.current && currentView) {
      const calendarApi = calendarRef.current.getApi();
      const currentCalendarView = calendarApi.view.type;

      // Only change view if it's different from the current one
      if (currentCalendarView !== currentView) {
        calendarApi.changeView(currentView);
      }
    }
  }, [currentView]);

  // Error state
  if (isError) {
    return (
      <div className="calendar-loading w-100 h-100">
        <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: '#ef4444' }}></i>
        <p className="mb-0 ms-2 F-size16 text-bold">Error loading calendar: {error?.message || 'Unknown error'}</p>
      </div>
    );
  }

  // Show unified loading overlay for both API loading and view transitions
  const showLoading = isLoading || isTransitioning;
  const loadingMessage = isTransitioning ? 'Switching view...' : 'Loading calendar...';

  return (
    <div className="calendar-v2-view">
      {showLoading && (
        <div className="calendar-transition-overlay visible">
          <i className="pi pi-spin pi-spinner"></i>
          <p>{loadingMessage}</p>
        </div>
      )}
      <FullCalendar ref={calendarRef} {...calendarOptions} />
    </div>
  );
};

export default React.memo(CalendarView);
